/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { Container } from 'pixi.js';
import { KnotBlockerModel } from 'game/boardMVC/models/blockers/KnotBlockerModel';
import { RocketBlockerModel } from 'game/boardMVC/models/blockers/RocketBlockerModel';
import { MeasureTapeBlockerModel } from 'game/boardMVC/models/blockers/MeasureTapeBlockerModel';
import { TileView } from './TileView';
import { TileStackView } from './TileStackView';
import type { TileModel } from '../../models/board/TileModel';
import type { TileStackModel } from '../../models/board/TileStackModel';
import type { BlockerModel, BlockerView } from '../../types';
import { PlankBlockerView } from '../blockers/PlankBlockerView';
import { PlankBlockerModel } from '../../models/blockers/PlankBlockerModel';
import { AdBlockerModel } from '../../models/blockers/AdBlockerModel';
import { AdBlockerView } from '../blockers/AdBlockerView';
import { ScoreBlockerModel } from '../../models/blockers/ScoreBlockerModel';
import { TapeBlockerView } from '../blockers/TapeBlockerView';
import { TapeBlockerModel } from '../../models/blockers/TapeBlockerModel';
import { ScoreBlockerFieldView } from '../blockers/ScoreBlockerFieldView';
import { ScoreBlockerStackView } from '../blockers/ScoreBlockerStackView';
import { KnotBlockerView } from '../blockers/KnotBlockerView';
import { BirdHouseBlockerModel } from '../../models/blockers/BirdHouseBlockerModel';
import { BirdHouseBlockerView } from '../blockers/BirdHouseBlockerView';
import { StarBlockerModel } from '../../models/blockers/StarBlockerModel';
import { StarBlockerView } from '../blockers/StarBlockerView';
import { RocketBlockerView } from '../blockers/RocketBlockerView';
import { CoinBlockerModel } from '../../models/blockers/CoinBlockerModel';
import { CoinBlockerView } from '../blockers/CoinBlockerView';
import { MeasureTapeBlockerView } from '../blockers/MeasureTapeBlockerView';

export class BoardViewFactory {
  private readonly stackViews = new Map<string, TileStackView>();

  private readonly tileViews = new Map<string, TileView>();

  private readonly blockerViews = new Map<string, BlockerView>();

  public createTileView(model: TileModel): TileView {
    const view = new TileView(model);
    this.tileViews.set(model.id, view);
    view.once('destroyed', () => {
      if (this.tileViews.get(model.id) !== view) return;
      this.tileViews.delete(model.id);
    });
    return view;
  }

  public createStackView(model: TileStackModel): TileStackView {
    const view = new TileStackView(model);
    this.stackViews.set(model.id, view);
    view.once('destroyed', () => {
      if (this.stackViews.get(model.id) !== view) return;
      this.stackViews.delete(model.id);
    });

    model.getTiles().forEach((tile) => {
      view.addTileView(this.createTileView(tile));
    });

    if (model.blocker) {
      view.addBlockerView(this.createBlockerView(model, model.blocker));
    }

    if (model.underTileBlocker) {
      view.addBlockerView(this.createBlockerView(model, model.underTileBlocker));
    }

    return view;
  }

  protected createBlockerViewInstance(stack: TileStackModel, model: BlockerModel): BlockerView | null {
    let view: BlockerView | null = null;

    if (model instanceof PlankBlockerModel) {
      view = new PlankBlockerView(model);
    } else if (model instanceof TapeBlockerModel) {
      view = new TapeBlockerView(model, this.stackViews.get(stack.id)!);
    } else if (model instanceof AdBlockerModel) {
      view = new AdBlockerView(model);
    } else if (model instanceof ScoreBlockerModel) {
      view = stack.getTileCount() === 0 ? new ScoreBlockerFieldView(model) : new ScoreBlockerStackView(model);
    } else if (model instanceof KnotBlockerModel) {
      view = new KnotBlockerView(model, this.stackViews.get(stack.id)!);
    } else if (model instanceof BirdHouseBlockerModel) {
      view = new BirdHouseBlockerView(model);
    } else if (model instanceof StarBlockerModel) {
      view = new StarBlockerView(model);
    } else if (model instanceof CoinBlockerModel) {
      view = new CoinBlockerView(model);
    } else if (model instanceof MeasureTapeBlockerModel) {
      view = new MeasureTapeBlockerView(model, this.stackViews.get(stack.id)!);
    } else if (model instanceof RocketBlockerModel) {
      view = new RocketBlockerView(model, this.stackViews.get(stack.id)!);
    }

    return view;
  }

  public createBlockerView(stack: TileStackModel, model: BlockerModel): BlockerView {
    const view = this.createBlockerViewInstance(stack, model);

    if (!view) {
      throw new Error(`ViewFactory: Unsupported blocker`);
    }

    this.blockerViews.set(model.id, view);
    view.once('destroyed', () => {
      if (this.blockerViews.get(model.id) !== view) return;
      this.blockerViews.delete(model.id);
    });

    return view;
  }

  public getBlockerView(id: string): BlockerView | null {
    return this.blockerViews.get(id) ?? null;
  }

  public getTileView(id: string): TileView | null {
    return this.tileViews.get(id) ?? null;
  }

  public getStackView(id: string): TileStackView | null {
    return this.stackViews.get(id) ?? null;
  }

  public destroyAllViews(): void {
    [...this.stackViews.values(), ...this.tileViews.values()].forEach((view: Container) => {
      view.destroy({ children: true });
    });
  }
}
